import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useQueryClient} from '@tanstack/react-query'
import {useCallback, useEffect, useMemo, useRef} from 'react'
import {HTTP_STATUS_OK, SystemRole} from '../../../Constants'
import {useAppConfig} from '../../../context/app/AppContext'
import {
    QueryCRUDTypes,
    QueryKeyManager,
} from '../../../query-manager/QueryKeysManager'
import {QueryKeyTypes} from '../../../query-manager/QueryKeyTypes'
import {useAuth} from '../../authentication/AuthProvider'

import {ReactComponent as DuoUserGroup} from '../../../assets/icons/DuoUserGroup.svg'
import {useUserHospital} from '../../../context/hospital/UserHospitalContext'
import TranslateUtils from '../../../i18n/TranslateUtils'
import {getClientId} from '../../authentication/AuthUtils'
import PageContentWrapper from '../../layout/PageContentWrapper'
import {useRoleContext} from '../../role/RoleProvider'
import {
    usePageSupportTeams,
    useSupportTeamCRUD,
} from '../../support-teams/query-keys/SupportTeamKeysManager'
import {getPageSupportTeams} from '../../support-teams/SupportTeamEndPoint'
import SupportTeamForm from '../../support-teams/SupportTeamsForm'
import {useImportNotificationContext} from '../../table-utils/importForm/ImportNotificationProvider'
import {
    filterDataTableEvent,
    filterEventTypes,
    getFilterEventKey,
    removeFilterChangeListener,
} from '../broadcast/FilterEvent'
import {exportColumnTypes, exportRecordTypes, exportTypes} from '../Constant'
import StyledCardListWrapper from '../StyledCardListWrapper'
import StyledDeleteDialog from '../StyledDeleteDialog'
import {
    getAdvancedFilter,
    getShouldRememberAdvancedFilter,
    tableTypes,
} from '../StyledTableUtils'
import {
    getSupportTeamEventKey,
    supportTeamEvent,
    supportTeamEventTypes,
} from './broadcast/SupportTeamEvent'

const StyledSupportTeamDataTable = () => {
    useSignals()

    const queryClient = useQueryClient()

    //#region Context
    const {user} = useAuth()
    const {userRole} = useAppConfig()
    const {currentHospital} = useUserHospital()
    const {supportTeamManagementPermissions} = useRoleContext()
    const {handleOnExportDatatable} = useImportNotificationContext()
    const {translateCommons} = TranslateUtils()
    //#endregion

    //#region Load Data from Local Storage
    const initialAdvancedFilterRequest = useMemo(() => {
        const didRememberSettings = getShouldRememberAdvancedFilter(
            tableTypes.supportTeams,
        )

        if (didRememberSettings) {
            const storedFilterRequest = getAdvancedFilter(
                tableTypes.supportTeams,
            )
            if (
                !!storedFilterRequest &&
                Object.keys(storedFilterRequest).length > 0
            ) {
                return storedFilterRequest
            }
        }

        return {}
    }, [])
    //#endregion

    //#region State Controls
    const pageable = useSignal({
        pageRows: 10,
        pageNumber: 0,
        sortField: 'createdAt',
        sortOrder: -1,
    })

    const filterRequest = useSignal({
        globalSearchValue: null,
        hospitalId:
            userRole === SystemRole.USER_WITH_ROLE ? currentHospital?.id : null,
        ...initialAdvancedFilterRequest,
    })

    // Pageable
    const handleChangeSortColumn = useCallback(
        e => {
            pageable.value = {
                ...pageable.value,
                sortField: e.sortField,
                sortOrder: e.sortOrder,
            }
        },
        [pageable.value.sortField, pageable.value.sortOrder],
    )

    const handleChangePageNumber = useCallback(
        newPageNumber => {
            pageable.value = {
                ...pageable.value,
                pageNumber: newPageNumber,
            }

            handlePrefetchDataByPage(newPageNumber + 1)
        },
        [pageable.value.pageNumber],
    )

    const handleChangePageRows = useCallback(
        newPageRows => {
            pageable.value = {
                ...pageable.value,
                pageRows: newPageRows,
                pageNumber: 0,
            }

            pagePrefetchRef.current = [0]

            handlePrefetchDataByPage(1)
        },
        [pageable.value.pageRows],
    )

    // Global Search Value
    const handleChangeGlobalSearch = useCallback(value => {
        filterRequest.value = {
            ...filterRequest.value,
            globalSearchValue: value || null,
            hospitalId:
                userRole === SystemRole.USER_WITH_ROLE
                    ? currentHospital?.id
                    : null,
        }
    }, [])

    // Filter Request
    const handleClearFilterRequest = useCallback(() => {
        filterRequest.value = {
            globalSearchValue: null,
            hospitalId:
                userRole === SystemRole.USER_WITH_ROLE
                    ? currentHospital?.id
                    : null,
        }
    }, [])

    const handleClearFilterRequestByKey = useCallback((key, value) => {
        filterRequest.value = {
            ...filterRequest.value,
            [key]: value,
        }
    }, [])

    const handleClearAdvancedFilterRequest = useCallback(() => {
        filterRequest.value = {
            globalSearchValue: filterRequest.value?.globalSearchValue || null,
            hospitalId:
                userRole === SystemRole.USER_WITH_ROLE
                    ? currentHospital?.id
                    : null,
        }
    }, [])

    // Table Action CRUD
    const formRef = useRef(null)
    const deleteRef = useRef(null)

    const {mutateAsync: deleteSupportTeams} = useSupportTeamCRUD(
        QueryCRUDTypes.delete,
        user?.id,
    )

    const handleAddNew = useCallback(() => {
        formRef.current?.showForm()
    }, [])

    const visible = useSignal(false)
    const handleOnHideDeleteDialog = data => {
        // if (!data) {
        //     deleteRef.current = null
        // } else {
        //     deleteRef.current = data
        // }

        visible.value = true
    }

    const handleOnDelete = async data => {
        if (!!data && !!data?.id) {
            const response = await deleteSupportTeams(data)

            if (response === HTTP_STATUS_OK) {
                const {supportTeamKeys} = QueryKeyManager(
                    QueryKeyTypes.supportTeam,
                    user?.id,
                )

                //TODO: Temporary Refetch Table
                queryClient.invalidateQueries(supportTeamKeys.pattern())
            }
        }
    }

    const handleAction = rowValue => {
        formRef.current?.showForm({...rowValue})
    }
    //#endregion

    //#region Check Permission
    const handleCheckPermissionByKey = useCallback(
        key => {
            if (!key) {
                return false
            }

            if (
                userRole === SystemRole.ADMIN ||
                supportTeamManagementPermissions?.includes(key)
            ) {
                return true
            }

            return false
        },
        [supportTeamManagementPermissions, userRole],
    )
    //#endregion

    //#region Data Controls
    const getCurrentQueryKey = ({newPageNumber, newPageRows}) => {
        const {supportTeamKeys} = QueryKeyManager(
            QueryKeyTypes.supportTeam,
            user?.id,
        )

        return supportTeamKeys.page({
            hospitalId:
                userRole === SystemRole.USER_WITH_ROLE
                    ? currentHospital?.id
                    : null,
            pageNumber:
                newPageNumber !== null && typeof newPageNumber === 'number'
                    ? newPageNumber
                    : pageable.value.pageNumber,
            pageRows:
                newPageRows !== null && typeof newPageNumber === 'number'
                    ? newPageRows
                    : pageable.value.pageRows,
            sortField: pageable.value.sortField,
            sortOrder: pageable.value.sortOrder,
            filterRequest: {
                ...filterRequest.value,
            },
        })
    }

    const {data: queryData, isFetched} = usePageSupportTeams(
        userRole === SystemRole.USER_WITH_ROLE ? currentHospital?.id : null,
        user?.id,
        pageable.value.pageNumber,
        pageable.value.pageRows,
        pageable.value.sortField,
        pageable.value.sortOrder,
        filterRequest.value,
        true,
    )

    const dataList = useRef([])
    const currentDataList = useMemo(() => {
        dataList.current = queryData?.content || []

        return queryData?.content || []
    }, [queryData?.content])

    // Page Rows from Pagination
    const rowsOptionHoveredRef = useRef([10])
    const handlePrefetchDataByRowsOption = useCallback(rows => {
        if (!!user?.id && !rowsOptionHoveredRef.current?.includes(rows)) {
            rowsOptionHoveredRef.current.push(rows)

            queryClient.prefetchQuery({
                queryKey: getCurrentQueryKey({
                    newPageNumber: 0,
                    newPageRows: rows,
                }),
                queryFn: () =>
                    getPageSupportTeams(
                        user?.id,
                        0,
                        rows,
                        pageable.peek().sortField,
                        pageable.peek().sortOrder,
                        filterRequest.peek(),
                    ),
            })
        }
    }, [])

    const handleOnDetailRowClick = id => {
        if (!!id) {
            const selectedRowData = dataList.current?.find(row => row.id === id)
            if (!!selectedRowData) {
                formRef.current?.showForm(selectedRowData)
            }
        }
    }

    // Page Number from Pagination
    const pagePrefetchRef = useRef([0])
    const handlePrefetchDataByPage = useCallback(pageNumber => {
        if (!!user?.id && !pagePrefetchRef.current?.includes(pageNumber)) {
            pagePrefetchRef.current.push(pageNumber)

            queryClient.prefetchQuery({
                queryKey: getCurrentQueryKey({
                    newPageNumber: pageNumber,
                    newPageRows: pageable.peek().pageRows,
                }),
                queryFn: () =>
                    getPageSupportTeams(
                        user?.id,
                        pageNumber,
                        pageable.peek().pageRows,
                        pageable.peek().sortField,
                        pageable.peek().sortOrder,
                        filterRequest.peek(),
                    ),
            })
        }
    }, [])
    //#endregion

    //#region Event Listener on State Changes
    useEffect(() => {
        // TABLE
        supportTeamEvent.on(
            getSupportTeamEventKey(supportTeamEventTypes.TABLE_CHANGE),
            data => {
                if (
                    !data ||
                    !data?.id ||
                    !data?.clientId ||
                    data?.clientId !== getClientId()
                ) {
                    return
                }

                if (
                    userRole === SystemRole.USER_WITH_ROLE &&
                    (!data?.hospitalId ||
                        data?.hospitalId !== currentHospital?.id)
                ) {
                    return
                }

                const {supportTeamKeys} = QueryKeyManager(
                    QueryKeyTypes.supportTeam,
                    user?.id,
                )

                queryClient.invalidateQueries({
                    queryKey: supportTeamKeys.pattern(),
                })
            },
        )

        // PAGE
        handlePrefetchDataByPage(pageable.value.pageNumber + 1)

        // FILTER
        filterDataTableEvent.on(
            `${getFilterEventKey(
                tableTypes.supportTeams,
                filterEventTypes.advanced,
            )}`,
            newFilterConfig => {
                if (!newFilterConfig || typeof newFilterConfig !== 'object') {
                    return
                }

                if (Object.keys(newFilterConfig).length === 0) {
                    handleClearAdvancedFilterRequest()

                    return
                }

                filterRequest.value = {
                    ...filterRequest.value,
                    ...newFilterConfig,
                }
            },
        )

        return () => {
            removeFilterChangeListener(
                tableTypes.supportTeams,
                filterEventTypes.advanced,
            )
        }
    }, [])
    //#endregion

    //#region Export Data
    const handleRetrieveExportData = async (
        {
            exportType = exportTypes.excel, // csv, excel, pdf
            exportRecordType = exportRecordTypes.allRecords, // all, currentInView
            exportColumns = exportColumnTypes.allColumns, // all, currentInView
        },
        columns,
    ) => {
        handleOnExportDatatable(
            {
                exportType: exportType,
                exportRecordType: exportRecordType,
                exportColumns: exportColumns,
            },
            queryData,
            pageable.value,
            filterRequest.value,
            columns,
            tableTypes.supportTeams,
        )
    }
    //#endregion

    //#region template
    const template = item => {
        return (
            <div className="h-full">
                <div className="flex gap-2">
                    {' '}
                    <DuoUserGroup height={30} width={30} />{' '}
                    <div className="mb-2 font-bold text-2xl ">
                        {item?.name ? item.name : 'N/a'}
                    </div>
                </div>

                <div className="flex gap-2 mb-2">
                    <div className="text-md  font-medium  flex gap-2  items-center bg-orange-500 text-white p-1 border-round-sm ">
                        <span className="font-bold">
                            {' '}
                            {item?.hospitalName || 'N/a'}
                        </span>
                    </div>

                    <div className="text-md  font-medium  flex gap-2  items-center bg-blue-500 text-white p-1 border-round-sm px-2">
                        <span className="font-bold">
                            {item?.numberOfParticipants
                                ? `${item?.numberOfParticipants} participants`
                                : 'N/A'}
                        </span>
                    </div>
                </div>
                <h6 className="m-0 p-0 mb-2  text-600">Participants</h6>
                <div className="flex flex-wrap  py-2 mb-2 border-1 border-dashed border-round-lg border-gray-200 p-2 ">
                    {item?.selectedParticipants &&
                    item?.selectedParticipants?.length > 0
                        ? item?.selectedParticipants?.map((item, idx) => (
                              <span
                                  className={`text-md text-blue-500 font-bold bg-blue-50  px-2 py-1 border-round mr-1 mb-1`}>
                                  {item?.fullName}
                              </span>
                          ))
                        : 'No symptoms at the moment.'}
                </div>
                <h6 className="flex align-items-center gap-1 m-0 p-0 mb-2  text-600">
                    Description
                </h6>
                <div className="flex flex-wrap  py-2 border-1  border-dashed border-round-lg border-gray-200 p-2 mb-2">
                    {item?.description ? item.description : 'N/a'}{' '}
                </div>
            </div>
        )
    }
    //#endregion
    console.log(queryData?.content)
    return (
        <PageContentWrapper>
            <SupportTeamForm ref={formRef} />

            <StyledCardListWrapper
                handleAddNew={handleAddNew}
                handleAction={handleAction}
                handleOnDelete={handleOnDelete}
                initialData={queryData?.content || []}
                className={`min-h-screen b`}
                template={template}
                title={translateCommons('Support Teams Management')}
                description={
                    'Manage the participating health care support teams.'
                }
            />
            <StyledDeleteDialog ref={deleteRef} onDelete={handleOnDelete} />
        </PageContentWrapper>
    )
}

export default StyledSupportTeamDataTable
